import * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Input } from './input.js';

export interface ExpandingSearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className'
> {
  /**
   * Width class applied at rest, e.g. `'w-56'`. This is the box's PERMANENT
   * footprint in the surrounding flex layout — it never changes, so sibling
   * elements never reflow.
   */
  collapsedWidthClassName?: string;
  /**
   * Width class applied while focused, e.g. `'w-80'`. Only the absolutely
   * positioned overlay grows to this width; it visually floats over whatever
   * sits to the right rather than pushing it out of the way.
   */
  expandedWidthClassName?: string;
  /** Rendered directly below the input, e.g. a search-suggestions dropdown. */
  dropdown?: React.ReactNode;
  /** Extra classes merged onto the `<input>` itself. */
  inputClassName?: string;
}

/**
 * ExpandingSearchInput — a search box that grows wider on focus WITHOUT
 * reflowing sibling elements.
 *
 * The outer wrapper reserves a fixed-size footprint (`collapsedWidthClassName`)
 * in the normal document flow permanently. The actual interactive input lives
 * in an `absolute top-0 left-0` child on top of that footprint; only that
 * child's width (and a focus shadow/ring) change on focus, so it floats over
 * neighboring content instead of resizing the flex item other siblings see.
 *
 * Use this whenever a search box needs to share a row with other flex/wrap
 * siblings (filter pills, buttons) and an expand-on-focus affordance without
 * triggering layout shifts in that row.
 */
export function ExpandingSearchInput({
  collapsedWidthClassName = 'w-56',
  expandedWidthClassName = 'w-80',
  dropdown,
  inputClassName,
  onFocus,
  onBlur,
  ...inputProps
}: ExpandingSearchInputProps) {
  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <div className={cn('relative h-9', collapsedWidthClassName)}>
      <div
        data-testid="expanding-search-overlay"
        className={cn(
          'absolute top-0 left-0 transition-all duration-150 ease-out',
          isFocused ? cn(expandedWidthClassName, 'z-20') : cn(collapsedWidthClassName, 'z-0'),
        )}
      >
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            {...inputProps}
            className={cn(
              'h-9 pl-9 text-[13px]',
              isFocused ? 'shadow-lg ring-2 ring-primary/20' : '',
              inputClassName,
            )}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
          />
          {dropdown}
        </div>
      </div>
    </div>
  );
}
